import React, { useContext, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import CustomSelect from "shared/components/FormElements/Select";
import Input from "shared/components/FormElements/Input";

import { useForm } from "shared/hooks/form-hook";
import { useHttpClient } from "shared/hooks/http-hook";
import { AuthContext } from "shared/context/auth-context";

import PassengerSelection from "./PassengerSelection";
import FileUploadSection from "./FileUploadSection";

import { VALIDATOR_MIN, VALIDATOR_MAX } from "shared/util/validators";

import { personelServiceRouteTypesIncludeBoth_tr, personelServiceRouteTypesIncludeBoth_en } from "shared/util/types";
import AutoWizard from "components/ui/wizards/AutoWizard";
import { getCurrentLanguage } from "shared/utility/lang";
import { StoreContext } from "shared/context/store-context";
import ResultsStepCard from "./ResultsStepCard";
import { SERVICE_TYPES } from "@routebot/constants";

// Stable context to share state with step components without changing component identity
const AutomationStepContext = React.createContext(null);

// Stable Step 3 component to avoid remounts (which reset scroll)
function Steps3Component({ updateCanProceed, isProcessing }) {
  const { t } = useTranslation();
  const ctx = React.useContext(AutomationStepContext);

  React.useEffect(() => {
    const okCount = (ctx.selectedPassengers || []).filter((p) => p.readerStatus === "ok").length;
    updateCanProceed(okCount > 0 && !isProcessing);
  }, [ctx.selectedPassengers, isProcessing, updateCanProceed]);

  const allPassengersInSchool = ctx.getPassengers() || [];
  const addAllButtonText = ctx.texts.passsengerSelection.formatAddAllButtonText
    ? ctx.texts.passsengerSelection.formatAddAllButtonText(allPassengersInSchool.length)
    : t("createSchoolRoutesAutomation.addAllButton", { count: allPassengersInSchool.length });
  const addAllModalText = ctx.texts.passsengerSelection.formatAddAllModalText
    ? ctx.texts.passsengerSelection.formatAddAllModalText(allPassengersInSchool.length)
    : t("createSchoolRoutesAutomation.addAllModalText", { count: allPassengersInSchool.length });

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
        marginTop: "20px",
      }}
    >
      <PassengerSelection
        serviceType={ctx.serviceType}
        schoolName={ctx.schoolOptions.find((school) => school.value === ctx.selectedSchoolId)?.label}
        maxPassengersPerRoute={ctx.maxNumberofPassengersInARoute}
        routeType={ctx.routeType}
        routeTypeLabel={ctx.routeTypes_Options.find((option) => option.value === ctx.routeType)?.label}
        allPassengersInSchool={allPassengersInSchool}
        onUpdatePassengers={(passengers) => {
          ctx.setSelectedPassengers(passengers);
        }}
        allSelectedPassengers={ctx.selectedPassengers || []}
        disabled={isProcessing}
        title={ctx.texts.passsengerSelection.title}
        subtitle={ctx.texts.passsengerSelection.subtitle}
        selectLabel={ctx.texts.passsengerSelection.selectPassengerLabel}
        addButtonText={ctx.texts.passsengerSelection.addPassengerButtonText}
        completeButtonText={ctx.texts.passsengerSelection.completeButtonText}
        emptyStateText={ctx.texts.passsengerSelection.emptyStateText}
        passengerLabel={ctx.texts.passsengerSelection.passengerLabel}
        selectedPassengersTitle={ctx.texts.passsengerSelection.selectedPassengersTitle}
        maxPassengersCardTitle={ctx.texts.passsengerSelection.maxPassengersCardTitle}
        routeTypeCardTitle={ctx.texts.passsengerSelection.routeTypeCardTitle}
        selectedPassengersCountTitle={ctx.texts.passsengerSelection.selectedPassengersCountTitle}
        addAllButtonText={addAllButtonText}
        notFoundHeader={ctx.texts.passsengerSelection.notFoundHeader}
        notFoundDescription={ctx.texts.passsengerSelection.notFoundDescription}
        multipleHeader={ctx.texts.passsengerSelection.multipleHeader}
        multipleDescription={ctx.texts.passsengerSelection.multipleDescription}
        noLocationHeader={ctx.texts.passsengerSelection.noLocationHeader}
        noLocationDescription={ctx.texts.passsengerSelection.noLocationDescription}
        addAllModalTitle={ctx.texts.passsengerSelection.addAllModalTitle}
        addAllModalText={addAllModalText}
        modalCancelText={ctx.texts.passsengerSelection.modalCancelText}
        modalAddText={ctx.texts.passsengerSelection.modalAddText}
        toastNoLocation={ctx.texts.passsengerSelection.toastNoLocation}
        toastAdded={ctx.texts.passsengerSelection.toastAdded}
        toastAlreadySelected={ctx.texts.passsengerSelection.toastAlreadySelected}
      />
    </div>
  );
}

// Stable Step 1 component to avoid remounts while active
function Steps1Component({ updateCanProceed, isProcessing }) {
  const ctx = React.useContext(AutomationStepContext);

  React.useEffect(() => {
    const maxOk = parseInt(ctx.maxNumberofPassengersInARoute);
    const shiftOk = ctx.serviceType === SERVICE_TYPES.SHIFT ? !!ctx.selectedShiftId : true;
    const valid = !!ctx.selectedSchoolId && maxOk > 0 && maxOk <= 100 && shiftOk;
    updateCanProceed(valid);
  }, [ctx.selectedSchoolId, ctx.maxNumberofPassengersInARoute, updateCanProceed, ctx.selectedShiftId]);

  return (
    <div style={{ maxWidth: "750px", display: "flex", flexDirection: "column", width: "50%", marginTop: "50px" }}>
      <CustomSelect
        options={ctx.schoolOptions}
        id="school"
        onInput={(id, value) => {
          ctx.setSelectedSchoolId(value);
          if (ctx.selectedSchoolId !== value) {
            ctx.setSelectedPassengers([]);
            ctx.setSelectedShiftId(null);
          }
          // ctx.inputHandler("school", value, true);
          // if (ctx.selectedSchoolId !== value) {
          //   ctx.setSelectedPassengers([]);
          // }
        }}
        label={ctx.texts.schoolOptionsLabel}
        initialValue={ctx.schoolOptions.find((school) => school.value === ctx.selectedSchoolId)}
        disabled={isProcessing}
      />
      {ctx.serviceType === SERVICE_TYPES.SHIFT && (
        <div style={{ marginTop: "20px", width: "400px" }}>
          <CustomSelect
            options={ctx.getShiftOptions()}
            id="shift"
            label={
              ctx.texts.shiftOptionsLabel +
              " (" +
              (ctx?.schoolOptions?.find((school) => school?.value === ctx?.selectedSchoolId)?.label || "-") +
              ")"
            }
            initialValue={ctx.getShiftOptions().find((option) => option.value === ctx.selectedShiftId)}
            onInput={(id, value) => {
              ctx.setSelectedShiftId(value);
              //ctx.inputHandler("routeType", value, true);
            }}
          />
        </div>
      )}

      <div style={{ marginTop: "20px", width: "400px" }}>
        <CustomSelect
          options={ctx.routeTypes_Options}
          id="routeType"
          label={ctx.texts.routeTypeLabel}
          initialValue={ctx.routeTypes_Options.find((option) => option.value === ctx.routeType)}
          onInput={(id, value) => {
            ctx.setRouteType(value);
            //ctx.inputHandler("routeType", value, true);
          }}
        />
      </div>
      <div style={{ width: "400px", marginTop: "20px" }}>
        <Input
          id="maxNumberOfPassengersInARoute"
          element="input"
          type="number"
          label={ctx.texts.maxNumberOfPassengersInARouteLabel}
          validators={[VALIDATOR_MIN(1), VALIDATOR_MAX(100)]}
          initialValue={ctx.maxNumberofPassengersInARoute}
          initialValid={true}
          errorText={ctx.texts.maxNumberOfPassengersInARouteErrorText}
          onInput={(id, value) => {
            ctx.setMaxNumberofPassengersInARoute(value);
            //   ctx.inputHandler("maxNumberOfPassengersInARoute", value, true);
          }}
          elementStyle={{ width: "100px" }}
        />
      </div>
    </div>
  );
}

const AutomationBase = ({
  steps,
  schoolOptions,
  passengers,
  fileUploadConfig = {},
  serviceType,
  onProcessComplete,
  texts = {},
}) => {
  const { t } = useTranslation();
  const currentLanguage = getCurrentLanguage();
  const routeTypes_Options =
    currentLanguage === "tr" ? personelServiceRouteTypesIncludeBoth_tr : personelServiceRouteTypesIncludeBoth_en;
  const auth = useContext(AuthContext);
  const { schools, shifts, shiftOptions, UpdateVehicleRoutes, UpdatePersonelServices } = useContext(StoreContext);
  const { sendRequest } = useHttpClient();

  // Form states
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedSchoolId, setSelectedSchoolId] = useState(schoolOptions.length > 1 ? null : schoolOptions[0].value);
  const [selectedShiftId, setSelectedShiftId] = useState(null);
  const [maxNumberofPassengersInARoute, setMaxNumberofPassengersInARoute] = useState(15);
  const [routeType, setRouteType] = useState(routeTypes_Options[0].value);

  // Page States
  const [activeStep, setActiveStep] = useState(0);
  const [isCreatingRoutes, setIsCreatingRoutes] = useState(false);
  const [isFileAnalyzing, setIsFileAnalyzing] = useState(false);

  const [processId, setProcessId] = useState(null);
  const [processResults, setProcessResults] = useState(null);

  const [selectedPassengers, setSelectedPassengers] = useState([]);

  const autoWizardRef = React.useRef();

  const analyzeFileAndExtractPassengers = async (file) => {
    setIsFileAnalyzing(true);
    setSelectedFile(file);

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("school", selectedSchoolId);
      formData.append("routeType", routeType);

      const passengersFromFile = await sendRequest(
        `${process.env.REACT_APP_BACKEND_URL}/automation-runner/extractfile`,
        "POST",
        formData,
        null,
        false
      );

      // Otomatik olarak bir sonraki step'e geç
      setTimeout(() => {
        if (autoWizardRef.current && autoWizardRef.current.handleNext) {
          autoWizardRef.current.handleNext();
        }
      }, 50); // 500ms bekle, sonra geç

      setIsFileAnalyzing(false);
      setSelectedPassengers(passengersFromFile.passengers);
    } finally {
      setIsFileAnalyzing(false);
    }
  };

  const createRoutes = async () => {
    setIsCreatingRoutes(true);

    try {
      let distributionProcessId = null;
      const validPassengers = selectedPassengers.filter((passenger) => passenger.readerStatus === "ok");
      const response = await sendRequest(
        `${process.env.REACT_APP_BACKEND_URL}/automation-runner/createroutes`,
        "POST",
        JSON.stringify({
          school: schools.find((school) => school.id === selectedSchoolId),
          serviceType: serviceType,
          passengerIds: validPassengers.map((passenger) => passenger.id),
          routeType: routeType,
          maxNumberofPassengersInARoute: maxNumberofPassengersInARoute,
          shiftId: selectedShiftId,
        })
      );

      distributionProcessId = response.process.id;

      if (!distributionProcessId) {
        window.toast.error("Routes creation failed");
        return;
      }

      setProcessId(distributionProcessId);
      setIsCreatingRoutes(false);
    } catch (err) {
      console.log(err);
    }
    setIsCreatingRoutes(false);
  };

  useEffect(() => {
    const fetchProcessState = async () => {
      if (!processId) {
        return;
      }

      try {
        const resp = await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/process/${processId}`, "GET", null, {
          Authorization: "Bearer " + auth.token,
        });

        if (resp?.process?.processing === false && resp?.process?.errorCode === 0) {
          //await getProcessResults(resp.process);
          setProcessResults(resp.process);
          if (serviceType === SERVICE_TYPES.STUDENT) {
            await UpdateVehicleRoutes();
          } else {
            await UpdatePersonelServices();
          }

          setProcessId(null);
        } else if (resp?.process?.processing === false && resp?.process?.errorCode === 1) {
        }
      } catch (err) {
        setProcessId(null);
      }
    };
    fetchProcessState();

    const intervalId = setInterval(fetchProcessState, 5000);

    return () => {
      clearInterval(intervalId);
    };
  }, [processId, sendRequest, auth.token, UpdateVehicleRoutes, UpdatePersonelServices]);

  // const getProcessResults = async (process) => {
  //   try {
  //     const processType = process.processType;

  //     if (processType === "draft-routes") {
  //       const draftServices = process.draftServices;
  //       if (onProcessComplete) {
  //         onProcessComplete(draftServices);
  //       }
  //     }
  //   } catch (err) {}
  // };

  // Create form state based on formFields prop
  // eslint-disable-next-line no-unused-vars
  const [formState, inputHandler] = useForm(
    {
      school: { value: selectedSchoolId, isValid: true },
      file: { value: null, isValid: true },
      maxNumberOfPassengersInARoute: { value: maxNumberofPassengersInARoute, isValid: true },
      routeType: { value: routeType, isValid: true },
    },
    true
  );

  const getPassengers = () => {
    const currentSchool = selectedSchoolId;
    const currentSchoolPassengers = passengers.filter(
      (passenger) => passenger.school.toString() === currentSchool.toString()
    );
    return currentSchoolPassengers;
  };

  const getShiftOptions = () => {
    const selectedSchoolShiftIds = shifts
      .filter((shift) => shift?.school?.toString() === selectedSchoolId?.toString())
      ?.map((shift) => shift.id);

    const myshiftOptions = shiftOptions.filter((shift) => selectedSchoolShiftIds.includes(shift.value));

    return myshiftOptions;
  };

  // Step 1 now provided via stable Steps1Component

  const steps2Content = ({ updateCanProceed }) => {
    React.useEffect(() => {
      updateCanProceed(!isFileAnalyzing);
    }, [updateCanProceed, isFileAnalyzing]);

    return (
      <div
        style={{
          maxWidth: "750px",
          height: "100%",
          maxHeight: "100%",
          display: "flex",
          flexDirection: "column",
          marginTop: "50px",
          width: "50%",
        }}
      >
        <h4 style={{ marginTop: "0px" }}>{t("createSchoolRoutesAutomation.step2")}</h4>
        <p style={{ marginTop: "0px", marginBottom: "20px" }}>
          {t("createSchoolRoutesAutomation.uploadFileDescription")}
        </p>
        <p style={{ marginTop: "0px", marginBottom: "20px" }}>
          {t("createSchoolRoutesAutomation.manualSelectionDescription")}
        </p>
        <FileUploadSection
          title={fileUploadConfig.uploadFileTitle}
          exampleFileName={fileUploadConfig.exampleFileName}
          accept={fileUploadConfig.accept}
          selectedFile={selectedFile}
          disabled={isFileAnalyzing}
          isFileAnalyzing={isFileAnalyzing}
          analyzeButtonText={t("fileUploadSection.analyzeButtonText")}
          instructionText={t("fileUploadSection.instruction")}
          analyzingText={t("fileUploadSection.analyzing")}
          uploadedFileText={t("fileUploadSection.uploadedFile")}
          onAnalyze={async (file) => {
            try {
              const passengerInfo = await analyzeFileAndExtractPassengers(file);
              setSelectedPassengers(passengerInfo.passengers);
            } catch (error) {}
          }}
        />
      </div>
    );
  };

  // Step 3 now provided via stable component Steps3Component

  const steps4Content = ({ updateCanProceed }) => {
    return (
      <ResultsStepCard
        processId={processId}
        isCreatingRoutes={isCreatingRoutes}
        onProcessComplete={onProcessComplete}
        processResults={processResults}
        updateCanProceed={updateCanProceed}
      />
    );
  };

  const nextButtonTexts = [
    t("automations.next"),
    t("automations.next"),
    t("automations.createRoutesFor", {
      count: selectedPassengers.filter((passenger) => passenger.readerStatus === "ok").length,
    }),
    t("automations.finish"),
  ];

  const stepContent = [Steps1Component, steps2Content, Steps3Component, steps4Content];

  return (
    <React.Fragment>
      <div style={{ display: "flex", flexDirection: "column", marginTop: "10px", height: "100%" }}>
        {/* <HighlightedCard title={title} subtitle={subtitle} description={description} /> */}

        <AutomationStepContext.Provider
          value={{
            schoolOptions,
            texts,
            //inputHandler,
            serviceType,
            selectedSchoolId,
            setSelectedSchoolId,
            selectedShiftId,
            setSelectedShiftId,
            maxNumberofPassengersInARoute,
            setMaxNumberofPassengersInARoute,
            routeType,
            setRouteType,
            routeTypes_Options,
            selectedPassengers,
            setSelectedPassengers,
            getPassengers,
            getShiftOptions,
          }}
        >
          <AutoWizard
            ref={autoWizardRef}
            steps={steps}
            stepContent={stepContent}
            onComplete={() => {
              onProcessComplete(processResults);
            }}
            finishButtonText={texts.finishButtonText || "View Routes"}
            nextButtonText={nextButtonTexts[activeStep]}
            isProcessing={isFileAnalyzing || isCreatingRoutes}
            onStepChange={(step) => {
              setActiveStep(step);
              if (step === 3) {
                createRoutes();
              }
            }}
          />
        </AutomationStepContext.Provider>
      </div>
    </React.Fragment>
  );
};

export default AutomationBase;
